/**
 * Notifications for the Capacitor app.
 *
 * The Android webview has no Push API at all — measured on an API 36 emulator,
 * `'PushManager' in window` is false — so the app cannot use the Web Push path
 * in utils/push.js. Browsers, including installed PWAs, never reach this file
 * and keep working exactly as they did.
 *
 * The plugin returns an FCM registration token on Android but a raw APNs device
 * token on iOS, and the two are not interchangeable - firebase-admin rejects an
 * APNs token. So the platform goes up with the token and the backend keeps them
 * in separate fields, sending over FCM or APNs accordingly.
 *
 * Delivery is dormant until the backend has FIREBASE_SERVICE_ACCOUNT (Android)
 * or APNS_* (iOS) set; the client side below is safe either way, because
 * register() simply rejects when the platform is not wired up.
 */
import { Capacitor } from '@capacitor/core'
import userService from '../services/userService'
import { inAppNavigate } from './inAppNavigate'

/** The app cannot use Web Push, so settings must offer the FCM route instead. */
export const hasNativePush = Capacitor.isNativePlatform()

const loadPlugin = () => import('@capacitor/push-notifications')

let listenersAttached = false

// register() resolves as soon as the native call is made - it does NOT reject
// when iOS refuses. The refusal arrives on the separate 'registrationError'
// event, so without listening for it a failed registration leaves no trace at
// all: no token, no error, no log. That is exactly how this stayed invisible.
let lastError = null

// Whether a token from THIS install actually reached the server. The OS
// permission alone is not enough to call notifications on: permission can be
// granted while registration silently fails, which is exactly what happened on
// iOS - the settings screen said "notifications active" and hid the only button
// that could retry, so there was no way back from a failed registration.
const REGISTERED_KEY = 'pushTokenRegistered'

function setRegistered(on) {
    try {
        if (on) localStorage.setItem(REGISTERED_KEY, '1')
        else localStorage.removeItem(REGISTERED_KEY)
    } catch { /* private mode */ }
}

/** Called on sign-out: the stored token belongs to the account that left. */
export function clearPushRegistered() {
    setRegistered(false)
}

/** The reason the last registration attempt failed, or null. */
export function lastPushError() {
    return lastError
}

/** Whether the OS has already granted notification permission to the app. */
export async function nativePushGranted() {
    if (!hasNativePush) return false
    const { PushNotifications } = await loadPlugin()
    const status = await PushNotifications.checkPermissions()
    return status.receive === 'granted'
}

/**
 * Whether notifications are genuinely working: the OS granted permission AND a
 * token from this install reached the server. Settings uses this rather than the
 * permission alone, so a failed registration still offers a way to retry.
 */
export async function nativePushActive() {
    if (!hasNativePush) return false
    try {
        if (localStorage.getItem(REGISTERED_KEY) !== '1') return false
    } catch { return false }
    return nativePushGranted()
}

/** Asks the OS for permission. Call this from the settings toggle, not on boot. */
export async function requestNativePush() {
    if (!hasNativePush) return false
    const { PushNotifications } = await loadPlugin()
    const status = await PushNotifications.requestPermissions()
    if (status.receive !== 'granted') return false
    lastError = null
    await initNativePush()
    // register() returning is not success - iOS reports refusal asynchronously
    // on registrationError. Give it a moment to arrive so the settings toggle
    // can say what went wrong instead of silently claiming it worked.
    await new Promise(resolve => setTimeout(resolve, 1500))
    return lastError === null
}

/** Registers for push if permission is already granted. Called on every app start. */
export async function initNativePush() {
    if (!hasNativePush) return

    const { PushNotifications } = await loadPlugin()

    // Only ever *check* here. Asking on first launch, before the user has seen
    // the app, is the classic way to get the prompt denied for good.
    const status = await PushNotifications.checkPermissions()
    if (status.receive !== 'granted') return

    // initNativePush runs at boot and again when a session starts, and
    // addListener appends rather than replaces - without this the same token
    // gets POSTed once per call.
    if (listenersAttached) return PushNotifications.register()
    listenersAttached = true

    await PushNotifications.addListener('registration', async ({ value }) => {
        lastError = null
        try {
            await userService.registerFcmToken(value, Capacitor.getPlatform())
            setRegistered(true)
        } catch {
            // the token is re-offered on the next app open
            setRegistered(false)
        }
    })

    await PushNotifications.addListener('registrationError', (err) => {
        lastError = err?.error || err?.message || String(err)
        setRegistered(false)
        console.error('[push] registration failed:', lastError)
    })

    await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
        const url = notification?.data?.url
        if (url) inAppNavigate(url)
    })

    try {
        await PushNotifications.register()
    } catch (err) {
        lastError = err?.message || String(err)
        console.error('[push] register() threw:', lastError)
    }
}
